const configuredApiUrl = import.meta.env.VITE_API_URL?.trim();
const API_BASE_URL = configuredApiUrl || (import.meta.env.DEV ? 'http://localhost:4000/api' : '');

const ensureApiUrl = () => {
  if (!API_BASE_URL) {
    throw new Error('Backend API is not configured. Set VITE_API_URL in the deployed frontend service.');
  }
};

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly requestId?: string;

  constructor(message: string, status: number, code?: string, requestId?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

export type ApiAuthUser = {
  id: string;
  name: string;
  email: string;
};

type AuthResponse = {
  user: ApiAuthUser;
  accessToken: string;
  refreshToken?: string;
};

type ErrorBody = {
  code?: string;
  message?: string | string[];
  requestId?: string;
};

const REQUEST_TIMEOUT_MS = 15_000;
const MAX_SAFE_RETRIES = 2;

const wait = (delayMs: number) => new Promise<void>((resolve) => window.setTimeout(resolve, delayMs));

const isRetryableStatus = (status: number) => [408, 429, 502, 503, 504].includes(status);

const request = async <T>(path: string, options: RequestInit = {}, authenticated = false): Promise<T> => {
  ensureApiUrl();
  const token = localStorage.getItem('talentsim-token');

  const method = (options.method ?? 'GET').toUpperCase();
  const canRetry = method === 'GET';
  let response: Response | undefined;
  let lastNetworkError: unknown;

  for (let attempt = 0; attempt <= (canRetry ? MAX_SAFE_RETRIES : 0); attempt += 1) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      response = await fetch(`${API_BASE_URL}${path}`, {
        ...options,
        signal: options.signal ?? controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...(authenticated && token ? { Authorization: `Bearer ${token}` } : {}),
          ...options.headers,
        },
      });
      if (!canRetry || !isRetryableStatus(response.status) || attempt === MAX_SAFE_RETRIES) break;
      await wait(250 * 2 ** attempt);
    } catch (error) {
      lastNetworkError = error;
      if (!canRetry || attempt === MAX_SAFE_RETRIES) break;
      await wait(250 * 2 ** attempt);
    } finally {
      window.clearTimeout(timeout);
    }
  }

  if (!response) {
    if (lastNetworkError instanceof DOMException && lastNetworkError.name === 'AbortError') {
      throw new ApiError('The request timed out. Please try again.', 408, 'REQUEST_TIMEOUT');
    }
    throw new ApiError('Unable to reach the server. Check your connection and try again.', 0, 'NETWORK_ERROR');
  }

  if (!response.ok) {
    const body = await response.json().catch(() => null) as ErrorBody | null;
    const message = Array.isArray(body?.message) ? body.message.join(', ') : body?.message;
    throw new ApiError(message || 'Request failed', response.status, body?.code, body?.requestId ?? response.headers.get('x-request-id') ?? undefined);
  }

  return response.json() as Promise<T>;
};

export const authApi = {
  login: (email: string, password: string) =>
    request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  register: (name: string, email: string, password: string) =>
    request<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ name, email, password }),
    }),
  refresh: () =>
    request<{ accessToken: string }>('/auth/refresh', {
      method: 'POST',
    }, true),
};

export const aiApi = {
  evaluateAnswer: (input: {
    question: string;
    answer: string;
    role: string;
    interviewType: string;
    personality?: string;
    wpm?: number;
    fillerCount?: number;
    context?: Array<{ question: string; answer: string }>;
  }) => request<{
    score: number;
    feedback: string;
    source: 'openai' | 'local-heuristic';
    model: string;
    latencyMs: number;
    failureState?: string;
  }>('/ai/evaluate-answer', {
    method: 'POST',
    body: JSON.stringify(input),
  }, true),
};

export const storageApi = {
  uploadResume: async (file: File) => {
    ensureApiUrl();
    const token = localStorage.getItem('talentsim-token');
    const formData = new FormData();
    formData.append('file', file);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    let response: Response;
    try {
      response = await fetch(`${API_BASE_URL}/storage/resume`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
        signal: controller.signal,
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new ApiError('Resume upload timed out. Please try again.', 408, 'REQUEST_TIMEOUT');
      }
      throw new ApiError('Unable to upload the resume. Check your connection and try again.', 0, 'NETWORK_ERROR');
    } finally {
      window.clearTimeout(timeout);
    }

    if (!response.ok) {
      const body = await response.json().catch(() => null) as ErrorBody | null;
      const message = Array.isArray(body?.message) ? body.message.join(', ') : body?.message;
      throw new ApiError(message || 'Resume upload failed', response.status, body?.code, body?.requestId ?? response.headers.get('x-request-id') ?? undefined);
    }

    return response.json() as Promise<{ id: string; fileName: string; storagePath: string }>;
  },
};

  export type CreateInterviewInput = {
    role: string;
    company?: string;
    interviewType: string;
    difficulty: string;
  };

  export type CreateAnswerInput = {
    questionText: string;
    transcript: string;
      aiScore?: number;
      aiFeedback?: string;
      evaluationSource?: 'openai' | 'local-heuristic' | 'client-fallback';
      evaluationModel?: string;
      evaluationLatencyMs?: number;
      evaluationFailureState?: string;
    wpm: number;
    fillerCount: number;
    detectedFillers?: string[];
    cameraSamples?: Array<{
      timestampSeconds: number;
      eyeContactScore: number;
      faceDetected: boolean;
      posture: string;
      confidence: number;
      modelVersion: string;
    }>;
    pauseCount: number;
    durationSeconds: number;
  };

  export const interviewsApi = {
    create: (input: CreateInterviewInput) =>
      request<{ id: string }>('/interviews', {
        method: 'POST',
        body: JSON.stringify(input),
      }, true),
    addAnswer: (sessionId: string, input: CreateAnswerInput) =>
      request(`/interviews/${sessionId}/answers`, {
        method: 'POST',
        body: JSON.stringify(input),
      }, true),
    complete: (sessionId: string) =>
      request(`/interviews/${sessionId}/complete`, { method: 'POST' }, true),
    list: () =>
      request<Array<{
        id: string;
        role: string;
        company: string | null;
        interviewType: string;
        status: string;
        createdAt: string;
        answers: Array<{
          aiScore: number | null;
          wpm: number | null;
          fillerCount: number | null;
          transcript: string;
        }>;
      }>>('/interviews', { method: 'GET' }, true),
  };
